import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Pencil, Save, X, Upload, ExternalLink } from 'lucide-react';
import api, { mediaUrl, uploadImage, errText } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { LIBRARY_CATEGORIES } from '../OshoLibrary';

const BLANK = {
  title: '', url: '', description: '', category: 'library', thumbnail_url: '',
  author: '', is_featured: false, is_published: true, sort_order: 0,
};

const AdminLibrary = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // link object or {} for new
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/library/links'); setItems(r.data || []); }
    catch (e) { toast({ title: 'Failed to load', description: errText(e) }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(BLANK); setEditing({}); };
  const openEdit = (it) => { setForm({ ...BLANK, ...it }); setEditing(it); };
  const close = () => { setEditing(null); setForm(BLANK); };

  const save = async () => {
    if (!form.title.trim() || !form.url.trim()) return toast({ title: 'Title and URL are required' });
    setSaving(true);
    try {
      const payload = { ...form, sort_order: parseInt(form.sort_order, 10) || 0 };
      if (editing && editing.id) await api.patch(`/admin/library/links/${editing.id}`, payload);
      else await api.post('/admin/library/links', payload);
      toast({ title: 'Saved 🌸' }); close(); load();
    } catch (e) { toast({ title: 'Could not save', description: errText(e) }); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try { await api.delete(`/admin/library/links/${it.id}`); toast({ title: 'Deleted' }); load(); }
    catch (e) { toast({ title: 'Delete failed', description: errText(e) }); }
  };

  const labelFor = (key) => (LIBRARY_CATEGORIES.find((c) => c.key === key)?.label || key);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-mahogany" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-library-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif-display text-3xl text-burgundy">Osho Library</h1>
          <p className="text-mahogany/70 text-sm">Post links to books, audio, videos, playlists and other resources.</p>
        </div>
        <button onClick={openNew} className="pill-btn" data-testid="library-add-btn"><Plus className="w-4 h-4" /> Add link</button>
      </div>

      {items.length === 0 ? (
        <p className="text-mahogany/60">No links yet. Click “Add link” to post your first resource.</p>
      ) : (
        <div className="rounded-2xl border border-rose/40 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-rose-soft/40 text-mahogany/80 text-left">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-rose/30" data-testid={`library-row-${it.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-burgundy flex items-center gap-2">
                      {it.is_featured && <span className="text-[10px] uppercase tracking-widest bg-mahogany text-cream px-1.5 py-0.5 rounded-full">★</span>}
                      {it.title}
                    </div>
                    <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-xs text-rose-italic inline-flex items-center gap-1 hover:underline">{it.url.slice(0, 48)}{it.url.length > 48 ? '…' : ''} <ExternalLink className="w-3 h-3" /></a>
                  </td>
                  <td className="px-4 py-3 text-mahogany/80">{labelFor(it.category)}</td>
                  <td className="px-4 py-3">{it.is_published ? <span className="text-green-700">Published</span> : <span className="text-mahogany/50">Hidden</span>}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(it)} className="p-2 hover:bg-rose-soft/50 rounded-lg" data-testid={`library-edit-${it.id}`}><Pencil className="w-4 h-4 text-burgundy" /></button>
                    <button onClick={() => remove(it)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`library-delete-${it.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={close} data-testid="library-modal">
          <div className="bg-cream rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif-display text-2xl text-burgundy">{editing.id ? 'Edit link' : 'Add link'}</h2>
              <button onClick={close}><X className="w-5 h-5 text-mahogany/70" /></button>
            </div>
            <div className="space-y-3">
              <Field label="Title *"><input value={form.title} onChange={(e) => set('title', e.target.value)} className="inp" data-testid="library-f-title" /></Field>
              <Field label="URL *"><input value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://…" className="inp" data-testid="library-f-url" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Category">
                  <select value={form.category} onChange={(e) => set('category', e.target.value)} className="inp" data-testid="library-f-category">
                    {LIBRARY_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Author / Source"><input value={form.author} onChange={(e) => set('author', e.target.value)} className="inp" /></Field>
              </div>
              <Field label="Description"><textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} className="inp" /></Field>
              <Field label="Thumbnail (optional)">
                <div className="flex items-center gap-3">
                  {form.thumbnail_url
                    ? <img src={mediaUrl(form.thumbnail_url)} alt="" className="w-16 h-16 rounded-lg object-cover" />
                    : <div className="w-16 h-16 rounded-lg bg-rose-soft/60 flex items-center justify-center"><Upload className="w-4 h-4 text-mahogany/60" /></div>}
                  <label className="pill-btn-outline text-xs cursor-pointer py-1.5">Upload
                    <input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={async (e) => {
                      if (!e.target.files[0]) return;
                      try { const u = await uploadImage(e.target.files[0]); set('thumbnail_url', u); }
                      catch (err) { toast({ title: 'Upload failed', description: errText(err) }); }
                    }} />
                  </label>
                  {form.thumbnail_url && <button onClick={() => set('thumbnail_url', '')} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}
                </div>
                <p className="text-[11px] text-mahogany/55 mt-1">Not needed for YouTube/SoundCloud audio & video — they embed a player automatically.</p>
              </Field>
              <div className="grid grid-cols-3 gap-3 items-center">
                <Field label="Sort order"><input type="number" value={form.sort_order} onChange={(e) => set('sort_order', e.target.value)} className="inp" /></Field>
                <label className="flex items-center gap-2 text-sm text-burgundy mt-5"><input type="checkbox" checked={form.is_featured} onChange={(e) => set('is_featured', e.target.checked)} /> Featured</label>
                <label className="flex items-center gap-2 text-sm text-burgundy mt-5"><input type="checkbox" checked={form.is_published} onChange={(e) => set('is_published', e.target.checked)} /> Published</label>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={close} className="pill-btn-outline">Cancel</button>
              <button onClick={save} disabled={saving} className="pill-btn" data-testid="library-save-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} <Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.inp{width:100%;background:#fff;border:1px solid rgba(191,131,143,.5);border-radius:.6rem;padding:.5rem .75rem;font-size:.9rem;color:#4A1721}`}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
    {children}
  </label>
);

export default AdminLibrary;
